import React, { useCallback, useState } from 'react'
import { Flex, Modal, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import { VERTICAL_WELL_GAP } from '../../theme/constants'
import { SigningVerificationSection } from './signing-verification-section'
import { WebhookList } from './webhook-list'
import { WebhookDrawer } from './webhook-drawer'
import { DomainWebhookService } from '../../services/domain-webhook.service'
import { simpleNotification } from '../../_utils/utils'
import { EventWebhook } from '@pushly/models/lib/structs/event-webhooks/event-webhook'

const { Text } = Typography

export const DomainEventWebhooksTab = (props: { domainId: number }) => {
    const { domainId } = props
    const webhookService = useService(DomainWebhookService)

    const [drawerVisible, setDrawerVisible] = useState(false)
    const [editingWebhook, setEditingWebhook] = useState<EventWebhook | null>(null)
    const [listKey, setListKey] = useState(0)
    const [signingKey, setSigningKey] = useState(0)
    const [hasSecret, setHasSecret] = useState<boolean | null>(null)

    const handleAddWebhook = useCallback(() => {
        setEditingWebhook(null)
        setDrawerVisible(true)
    }, [])

    const handleEditWebhook = useCallback((webhook: EventWebhook) => {
        setEditingWebhook(webhook)
        setDrawerVisible(true)
    }, [])

    const handleDrawerClose = useCallback(() => {
        setDrawerVisible(false)
        setEditingWebhook(null)
    }, [])

    const handleSaved = useCallback(() => {
        setListKey((prev) => prev + 1)
        if (!hasSecret) {
            setSigningKey((prev) => prev + 1)
            setHasSecret(true)
        }
    }, [hasSecret])

    const handleToggleEnabled = async (webhook: EventWebhook) => {
        const res = await webhookService.update(domainId, webhook.id, {
            enabled: !webhook.enabled,
        } as any)
        if (res.ok) {
            simpleNotification('success', webhook.enabled ? 'Webhook paused.' : 'Webhook resumed.')
            setListKey((prev) => prev + 1)
        }
    }

    const handleArchiveWebhook = (webhook: EventWebhook) => {
        Modal.confirm({
            title: 'Archive Webhook',
            content: (
                <Text>
                    This webhook will be archived. Events will stop being delivered to this endpoint immediately.
                </Text>
            ),
            okText: 'Archive',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: async () => {
                const res = await webhookService.archive(domainId, webhook.id)
                if (res.ok) {
                    simpleNotification('success', 'Webhook archived successfully.')
                    setListKey((prev) => prev + 1)
                }
            },
        })
    }

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP}>
            <SigningVerificationSection key={`svs-${signingKey}`} domainId={domainId} onSecretResolved={setHasSecret} />

            <div className="webhooks-section">
                <WebhookList
                    key={listKey}
                    domainId={domainId}
                    onAddWebhook={handleAddWebhook}
                    onEditWebhook={handleEditWebhook}
                    onArchiveWebhook={handleArchiveWebhook}
                    onToggleEnabled={handleToggleEnabled}
                />
            </div>

            <WebhookDrawer
                key={editingWebhook?.id ?? 'new'}
                domainId={domainId}
                visible={drawerVisible}
                onClose={handleDrawerClose}
                onSaved={handleSaved}
                editingWebhook={editingWebhook}
            />
        </Flex>
    )
}
